import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  BookOpen, 
  ArrowRight, 
  ExternalLink,
  Calendar,
  Tag,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const SUBSTACK_URL = 'https://hrnest26.substack.com';

const blogPosts = [
  {
    title: 'Building Confidence in Your Career Journey',
    excerpt: 'Career confidence isn\'t something you\'re born with. It\'s something you build — often slowly, and especially during transitions.',
    date: 'Feb 09, 2026',
    category: 'Career Growth',
    url: 'https://hrnest26.substack.com/p/building-confidence-in-your-career',
    image: 'https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fsubstack-post-media.s3.amazonaws.com%2Fpublic%2Fimages%2F4e6f60c7-5c3c-4962-8dc9-edf1395f22ee_1536x1024.png',
    content: `Career confidence isn't something you're born with. It's something you build — often slowly, and especially during transitions.

Whether you're entering the workforce, changing industries, or restarting after a pause, moments of doubt are normal. But they don't have to define you.

**Here are three mindset shifts that can help:**

**1. Recognize what you already bring.**

Even if you don't have years of experience, you bring value. Your ability to learn quickly, adapt, ask thoughtful questions, or bring fresh perspective — that matters. Take time to reflect on what you've accomplished, even if it feels small.

**2. Reframe "not enough" into "still growing."**

Confidence doesn't mean having all the answers. It means trusting yourself to figure things out. Shift from "I don't have enough experience" to "I'm building experience every day."

**3. Take action before you feel ready.**

The truth is, you may never feel fully ready — and that's okay. Applying for that job, speaking up in a meeting, or asking for help are all acts of courage. Confidence grows through action, not just thinking about it.

**You don't have to have it all figured out to move forward.**

Start where you are. Trust your ability to grow. And know that every small step you take is building the foundation for the career you deserve.

*What's one thing you've done recently that helped build your confidence? Share in the comments — you never know who might need to hear it.*`
  },
  {
    title: 'The Power of Professional Networking',
    excerpt: 'How to build meaningful connections that support your career goals. Networking isn\'t about collecting contacts—it\'s about building relationships.',
    date: 'Feb 09, 2026',
    category: 'Networking',
    url: 'https://hrnest26.substack.com/p/the-power-of-professional-networking',
    image: 'https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fsubstack-post-media.s3.amazonaws.com%2Fpublic%2Fimages%2Fe246d095-2332-41c4-ad76-85a0295c34fa_1536x1024.png',
    content: `Networking isn't about collecting contacts — it's about building relationships that support your growth.

For many people, the word "networking" feels uncomfortable, transactional, or even intimidating. But when done authentically, networking can open doors you didn't even know existed.

**Here's how to approach it with intention:**

**1. Start with curiosity, not expectation.**

Instead of thinking about what you can get, focus on what you can learn. Ask questions. Listen. Show genuine interest in other people's journeys. Relationships built on curiosity tend to last longer than ones built on quick gains.

**2. Show up consistently — even in small ways.**

You don't have to attend every event or send dozens of messages. A thoughtful comment, a quick "congratulations" on a milestone, or checking in after a few months can go a long way. Consistency builds trust.

**3. Give before you ask.**

Share an article, make an introduction, or offer encouragement when someone needs it. When you lead with generosity, people remember it — and they're more likely to support you when the time comes.

**4. Diversify your network.**

Don't just connect with people who do what you do. Seek out mentors, peers from other industries, and people at different stages in their careers. Diverse perspectives lead to richer opportunities.

**5. Follow up and follow through.**

If you say you'll send something or reconnect later, do it. Small actions like these build credibility over time.

**Remember: Networking isn't about being the most outgoing person in the room.** It's about showing up as yourself and being willing to invest in others.

*What's one networking tip that's worked for you? Drop it in the comments — let's learn together.*`
  },
  {
    title: 'Strategies for Maintaining Work-Life Balance',
    excerpt: 'Maintaining balance in today\'s work environment isn\'t about eliminating pressure — it\'s about learning how to move through it without losing yourself.',
    date: 'Feb 09, 2026',
    category: 'Personal Growth',
    url: 'https://hrnest26.substack.com/p/strategies-for-maintaining-work-life',
    image: 'https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fsubstack-post-media.s3.amazonaws.com%2Fpublic%2Fimages%2Ffa2fe44d-2deb-42a9-9518-1aaa116da147_1536x1024.png',
    content: `Maintaining balance in today's work environment isn't about eliminating pressure — it's about learning how to move through it without losing yourself.

Balance looks different for everyone. For some, it's leaving work at a certain time. For others, it's creating mental space even when your schedule is full. What matters is finding what works for you — and protecting it.

**Here are a few strategies to consider:**

**1. Set boundaries that honor your energy.**

You can't pour from an empty cup. Whether it's saying no to extra tasks, turning off notifications after hours, or blocking time on your calendar for rest — protecting your energy is essential, not selfish.

**2. Separate productivity from your worth.**

You are not your to-do list. It's easy to tie your value to how much you accomplish, but your worth isn't measured by output. Sometimes the most productive thing you can do is rest.

**3. Create rituals that signal transitions.**

When work and personal life blur together (especially for remote workers), rituals help. A short walk, changing clothes, or even lighting a candle can signal to your brain that it's time to shift gears.

**4. Prioritize what matters most — and let the rest go.**

Not everything is urgent. Not everything needs your best effort. Learning to distinguish between what truly matters and what can wait is a skill that protects your peace.

**5. Ask for support when you need it.**

You don't have to do it all alone. Lean on your community, delegate where you can, and don't be afraid to ask for help — at work or at home.

**Balance isn't a destination — it's an ongoing practice.**

Some weeks will feel more aligned than others. What matters is that you keep adjusting, keep learning, and keep prioritizing yourself.

*What's one boundary you've set recently to protect your peace? Let's talk about it in the comments.*`
  },
];

// Blog Post Modal Component
const BlogPostModal = ({ post, isOpen, onClose }) => {
  if (!isOpen || !post) return null;

  // Convert markdown-like formatting to JSX
  const formatContent = (content) => {
    return content.split('\n\n').map((paragraph, index) => {
      // Check if it's a bold header
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return (
          <h3 key={index} className="text-lg font-semibold text-hr-text-primary mt-6 mb-2">
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
      }
      // Check for italic text (wrapped in single *)
      if (paragraph.startsWith('*') && paragraph.endsWith('*') && !paragraph.startsWith('**')) {
        return (
          <p key={index} className="text-hr-text-secondary italic mt-4">
            {paragraph.replace(/^\*|\*$/g, '')}
          </p>
        );
      }
      // Regular paragraph with possible bold text
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={index} className="text-hr-text-secondary leading-relaxed mb-4">
          {parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={i} className="text-hr-text-primary">{part.replace(/\*\*/g, '')}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-300 hover:scale-110"
        >
          <X className="h-5 w-5 text-hr-text-secondary" />
        </button>

        <div className="flex flex-col md:flex-row max-h-[90vh]">
          {/* Image */}
          <div className="md:w-2/5 h-64 md:h-auto">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="md:w-3/5 p-8 overflow-y-auto max-h-[60vh] md:max-h-[90vh]">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-hr-purple-light text-hr-purple text-sm font-medium">
                {post.category}
              </span>
              <span className="text-hr-text-muted text-sm flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {post.date}
              </span>
            </div>

            <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mb-6">
              {post.title}
            </h2>

            <div className="prose prose-stone max-w-none">
              {formatContent(post.content)}
            </div>

            <div className="mt-8 pt-6 border-t border-stone-200">
              <a 
                href={post.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-hr-purple hover:text-hr-purple-dark font-medium transition-colors"
              >
                Read on Substack
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Blogs = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReadPost = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-purple-light/20 relative" data-testid="blogs-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-purple/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-teal/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-hr-purple/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-purple/10 mb-6 animate-bounce-subtle">
              <BookOpen className="h-4 w-4 text-hr-purple animate-pulse" />
              <span className="text-hr-purple text-sm font-medium">HR Nest Blog</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              Insights for Your <span className="text-gradient bg-gradient-to-r from-hr-purple to-hr-teal bg-clip-text text-transparent animate-gradient">Career Journey</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Explore our collection of articles, tips, and insights designed to help you grow professionally and personally.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="blogs-posts">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary mb-4 animate-fade-in-up">
              Latest Articles
            </h2>
            <p className="text-hr-text-secondary max-w-2xl mx-auto animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Stay informed with our latest insights on career development, networking, and work-life balance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Card 
                key={index}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden rounded-2xl animate-fade-in-up cursor-pointer"
                style={{ opacity: 0, animationDelay: `${index * 100}ms` }}
                onClick={() => handleReadPost(post)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-white/90 text-hr-purple text-xs font-medium shadow-lg">
                      {post.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-hr-text-muted text-sm mb-3">
                    <Calendar className="h-4 w-4" />
                    {post.date}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-hr-text-primary mb-3 group-hover:text-hr-purple transition-colors duration-300 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-hr-text-secondary text-sm leading-relaxed line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>
                  <button className="inline-flex items-center gap-2 text-hr-purple font-medium text-sm group-hover:gap-3 transition-all duration-300">
                    Read Article
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Subscribe CTA */}
          <div className="mt-16 text-center animate-fade-in-up stagger-3" style={{ opacity: 0 }}>
            <div className="inline-flex flex-col items-center gap-4 p-8 rounded-3xl bg-gradient-to-r from-hr-purple-light to-hr-teal-light">
              <h3 className="font-heading text-2xl font-semibold text-hr-text-primary">
                Want More Insights?
              </h3>
              <p className="text-hr-text-secondary max-w-md">
                Subscribe to our Substack for weekly articles on career growth, professional development, and more.
              </p>
              <a href={SUBSTACK_URL} target="_blank" rel="noopener noreferrer">
                <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105">
                  Subscribe on Substack
                  <ExternalLink className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Post Modal */}
      <BlogPostModal 
        post={selectedPost}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default Blogs;
